<script setup lang="ts">
import CardComponent from "@/components/CardComponent.vue";
import CardDisplay from "@/components/CardDisplay.vue";
import LoadingModal from "@/components/LoadingModal.vue";
import ModalComponent from "@/components/ModalComponent.vue";
import ShadowBox from "@/components/ShadowBox.vue";
import StyledButton from "@/components/StyledButton.vue";
import StyledCheckbox from "@/components/StyledCheckbox.vue";
import StyledInput from "@/components/StyledInput.vue";
import { useErrorCodeOpenPay } from "@/modules/hotels/composables/useErrorCodeOpenPay";
import { OpenPayCard } from "@/interfaces/openPayCard";
import useUserOpenPayCard from "@/modules/users/composables/useUserOpenPayCard";
import useCompanyOpenPayCard from "@/modules/companies/composables/useCompanyOpenPayCard";
import useCompanyBillingInformation from "@/modules/companies/composables/useCompanyBillingInformation";
import { User } from "@/modules/users/interfaces/user";
import useHasPermissionUser from "@/composables/useHasPermissionUser";
import useAuthUser from "@/composables/useAuthUser";
import { useQueryClient } from "@tanstack/vue-query";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { computed, reactive, ref } from "vue";
import { useToast } from "vue-toastification";

const openPay = window.OpenPay;

interface Props {
  user: User;
}

openPay.setId(import.meta.env.VITE_OPENPAY_ID);
openPay.setApiKey(import.meta.env.VITE_OPENPAY_KEY);
openPay.setSandboxMode(import.meta.env.VITE_OPENPAY_PRODUCTION_MODE !== 'true');

const props = defineProps<Props>();
const toast = useToast();

const queryClient = useQueryClient();
const { getErrorCode } = useErrorCodeOpenPay();
const deviceDataId = openPay.deviceData.setup("formId");

const {
  cards: userCards,
  createCardAsync: createUserCardAsync,
  createCardLoading: createUserCardLoading,
  isLoading: isUserCardsLoading,
} = useUserOpenPayCard({
  params: {
    user_id: props.user.id,
  },
  immediate: true,
});

const hasCompany = computed(() => !!props.user.companyId);

const {
  cards: companyCards,
  createCardAsync: createCompanyCardAsync,
  createCardLoading: createCompanyCardLoading,
  isLoading: isCompanyCardsLoading,
} = useCompanyOpenPayCard({
  params: {
    companyId: props.user.companyId,
  },
  immediate: hasCompany.value,
});

const cards = computed<OpenPayCard[]>(() => [
  ...(userCards.value || []),
  ...(hasCompany.value ? companyCards.value || [] : []),
]);

const isLoading = computed(() => isUserCardsLoading.value || (hasCompany.value && isCompanyCardsLoading.value));

const { data: companyBillingData } = useCompanyBillingInformation({
  params: { id: props.user.companyId },
  immediate: hasCompany.value,
});

const hasCompanyBillingInfo = computed(() => !!companyBillingData.value);

const { hasPermissionUser } = useHasPermissionUser();
const canCreateCards = computed(() => hasPermissionUser('create-cards', 'App\\Models\\User'));

const { user: loggedUser } = useAuthUser();
const isSelf = computed(() => props.user.id === loggedUser.value?.id);

const canAddCard = computed(() => !!props.user.email && (canCreateCards.value || isSelf.value));
const canAddCorporateCard = computed(() => hasCompany.value && canAddCard.value && hasCompanyBillingInfo.value);

const modalFormCard = ref(false);
const addTarget = ref<'user' | 'company'>('user');

const actionFormCard = async () => {
  modalFormCard.value = !modalFormCard.value;
};

const openAddModal = (target: 'user' | 'company') => {
  addTarget.value = target;
  modalFormCard.value = true;
};

const rules = {
  card_number: {
    required: helpers.withMessage(
      "El número de tarjeta es requerido",
      required,
    ),
    requiredIf: helpers.withMessage("El número de tarjeta es inválido", () => {
      return openPay.card.validateCardNumber(
        formModels.card_number.replace(/\s/g, ""),
      );
    }),
  },
  cvv2: {
    required: helpers.withMessage(
      "El código de seguridad es requerido",
      required,
    ),
    requiredIf: helpers.withMessage(
      "El código de seguridad es inválido",
      () => {
        return openPay.card.validateCVC(formModels.cvv2);
      },
    ),
  },
  expiry: {
    required: helpers.withMessage(
      "La fecha de expiración es requerida",
      required,
    ),
    requiredIf: helpers.withMessage(
      "La fecha de expiración es inválida",
      () => {
        return openPay.card.validateExpiry(
          formModels.expiry.expiration_month,
          formModels.expiry.expiration_year,
        );
      },
    ),
  },
  holder_name: {
    required: helpers.withMessage(
      "El nombre del titular es requerido",
      required,
    ),
    requiredIf: helpers.withMessage("El nombre del titular es inválido", () => {
      return !new RegExp(/[^a-zA-ZáéíóúñÁÉÍÓÚÑ\s]/g).test(
        formModels.holder_name,
      );
    }),
  },
};

const formModels = reactive({
  holder_name: "",
  card_number: "",
  cvv2: "",
  expiry: {
    expiration_year: "",
    expiration_month: "",
  },
  alias: "",
});

const formattedCardNumber = computed({
  get: () => formModels.card_number,
  set: (value: string) => {
    const digits = value.replace(/\D/g, '');
    const chunks: string[] = [];
    for (let i = 0; i < digits.length; i += 4) {
      chunks.push(digits.slice(i, i + 4));
    }
    formModels.card_number = chunks.join(' ');
  },
});



const v$ = useVuelidate(rules, formModels);
const cvvLength = computed(() => {
  return formModels.card_number.startsWith("3") ? 4 : 3;
});

const numberLength = computed(() => {
  return formModels.card_number.startsWith("3") ? 15 : 16;
});

const activated = ref(false);

const actionActivated = () => {
  activated.value = !activated.value;
  formModels.holder_name = activated.value ? props.user.fullName : "";
};

const suggestedAlias = computed(() => {
  if (formModels.alias.trim()) return formModels.alias;
  if (addTarget.value !== 'company') return '';
  return props.user.companyName
    ? `Tarjeta corporativa - ${props.user.companyName}`
    : 'Tarjeta corporativa';
});

const personalCards = computed(() =>
  (cards.value || []).filter((card: OpenPayCard) => card.ownership === 'user')
);

const corporateCards = computed(() =>
  (cards.value || []).filter((card: OpenPayCard) => card.ownership === 'company')
);

const canShowAddButton = computed(() =>
  (canAddCard.value && personalCards.value.length < 6) ||
  (canAddCorporateCard.value && corporateCards.value.length < 6)
);

type CardFilter = 'all' | 'user' | 'company';
const activeFilter = ref<CardFilter>('all');

const filteredCards = computed(() => {
  if (activeFilter.value === 'user') return personalCards.value;
  if (activeFilter.value === 'company') return corporateCards.value;
  return cards.value || [];
});

const filterOptions: { value: CardFilter; label: string; icon: string; color: string; count: () => number }[] = [
  { value: 'all', label: 'Todas', icon: 'credit_card', color: 'gray', count: () => cards.value?.length || 0 },
  { value: 'user', label: 'Personales', icon: 'person', color: 'blue', count: () => personalCards.value.length },
  { value: 'company', label: 'Corporativas', icon: 'business', color: 'emerald', count: () => corporateCards.value.length },
];

const emptyStateMessages: Record<CardFilter, { title: string; body: string }> = {
  all: {
    title: 'No hay tarjetas guardadas',
    body: 'Agrega una tarjeta para pagar reservas de hoteles y otros servicios.',
  },
  user: {
    title: 'No hay tarjetas personales',
    body: 'Las tarjetas personales facturan a tu nombre.',
  },
  company: {
    title: 'No hay tarjetas corporativas',
    body: 'Las tarjetas corporativas facturan a la empresa.',
  },
};

const isBackVisible = ref(false);

const actionBack = () => {
  isBackVisible.value = !isBackVisible.value;
};

const onSuccess = async (response: any) => {
  if (deviceDataId && response) {
    try {
      if (addTarget.value === "company") {
        await createCompanyCardAsync({
          params: {
            cardId: response.data.id,
            deviceId: deviceDataId,
            companyId: props.user.companyId,
            alias: suggestedAlias.value?.trim() || undefined,
          },
          config: {
            onSuccessToast: "Tarjeta guardada correctamente",
            onErrorToast: "Hubo un error al guardar la tarjeta, intente de nuevo",
            raiseToast: true,
          },
        });

        await queryClient.invalidateQueries(["companyCards", props.user.companyId]);
      } else {
        await createUserCardAsync({
          params: {
            cardId: response.data.id,
            deviceId: deviceDataId,
            user_id: props.user.id,
            alias: suggestedAlias.value?.trim() || undefined,
          },
          config: {
            onSuccessToast: "Tarjeta guardada correctamente",
            onErrorToast: "Hubo un error al guardar la tarjeta, intente de nuevo",
            raiseToast: true,
          },
        });

        await queryClient.invalidateQueries(["userCards", props.user.id]);
      }

      actionFormCard();

      formModels.card_number = "";
      formModels.holder_name = "";
      formModels.expiry.expiration_month = "";
      formModels.expiry.expiration_year = "";
      formModels.cvv2 = "";
      formModels.alias = "";
    } catch (error: any) {
      if (error.response?.data?.error) {
        toast.error(getErrorCode(error.response?.data?.error?.code));
        return;
      }
      toast.error(error.response?.data?.message);
    }
  }
};

const createCardLoading = computed(() =>
  addTarget.value === "company" ? createCompanyCardLoading.value : createUserCardLoading.value
);

const onError = async (response: any) => {
  if (response.data.error_code) {
    toast.error(getErrorCode(response?.data?.error_code));
    return;
  }
  toast.error(response?.data?.description);
};

const handleSave = async () => {
  for (const field of v$.value.$silentErrors) {
    if (!Array.isArray(field.$message)) {
      toast.error(field.$message);
      return;
    }
  }

  if (!v$.value.$invalid) {
    openPay.token.create(
      {
        ...formModels,
        card_number: formModels.card_number.replace(/\s/g, ""),
        expiration_month: formModels.expiry.expiration_month,
        expiration_year: formModels.expiry.expiration_year,
      },
      onSuccess,
      onError,
    );
  }
};

</script>

<template>
  <ShadowBox class="bg-white">
    <!-- Header -->
    <div class="flex items-center justify-between mb-5">
      <div class="flex items-center gap-3">
        <div class="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
          <span class="material-symbols-outlined text-blue-600">credit_card</span>
        </div>
        <div>
          <h2 class="text-base font-semibold text-gray-900">
            Tarjetas bancarias
          </h2>
          <p class="text-xs text-gray-500">
            {{ cards.length ?? 0 }} tarjetas registradas
          </p>
        </div>
      </div>

      <button
        v-if="canShowAddButton"
        type="button"
        class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 transition-colors"
        @click="openAddModal(canAddCard && personalCards.length < 6 ? 'user' : 'company')"
      >
        <span class="material-symbols-outlined text-[16px]">add</span>
        Agregar tarjeta
      </button>
    </div>

    <!-- Warning: no email -->
    <div
      v-if="!props.user.email"
      class="flex items-start gap-3 p-4 mb-4 bg-amber-50 border border-amber-200 rounded-xl"
    >
      <span class="material-symbols-outlined text-amber-500 text-base flex-shrink-0 mt-0.5">warning</span>
      <p class="text-sm text-amber-800">
        Este usuario no tiene correo registrado. Es necesario contar con al menos uno para agregar tarjetas.
      </p>
    </div>

    <!-- Warning: company without billing info -->
    <div
      v-if="hasCompany && canAddCard && !hasCompanyBillingInfo"
      class="flex items-start gap-3 p-4 mb-4 bg-amber-50 border border-amber-200 rounded-xl"
    >
      <span class="material-symbols-outlined text-amber-500 text-base flex-shrink-0 mt-0.5">warning</span>
      <p class="text-sm text-amber-800">
        La empresa no tiene información de facturación registrada. Es necesaria para poder agregar tarjetas corporativas.
      </p>
    </div>

    <!-- Card form -->
    <ModalComponent
      v-if="canAddCard || canAddCorporateCard"
      :modelValue="modalFormCard"
      :onUpdate:modelValue="actionFormCard"
    >
      <template #modalBody>
        <div class="p-6 max-w-lg mx-auto">
          <h2 class="font-bold text-xl text-gray-900 mb-1">
            {{ addTarget === 'company' ? 'Agregar tarjeta corporativa' : 'Agregar tarjeta' }}
          </h2>
          <p class="text-sm text-gray-500 mb-6">
            {{ addTarget === 'company'
              ? 'Ingresa los datos de la tarjeta. Se factura a la empresa.'
              : 'Ingresa los datos de la tarjeta. Se factura al usuario.' }}
          </p>

          <div class="flex items-center justify-center mb-6">
            <div class="w-full">
              <CardDisplay
                :userName="formModels.holder_name"
                :form="{
                  expDate: {
                    month: Number(formModels.expiry.expiration_month),
                    year: Number(formModels.expiry.expiration_year),
                  },
                }"
                :cvv="formModels.cvv2"
                :number="formModels.card_number"
                :isBackVisible="isBackVisible"
                @toggleBackCard="actionBack"
                :isForm="true"
              />
            </div>
          </div>

          <!-- data-screenshot-exclude: dato de tarjeta fuera de la captura del widget de bugs (ver useScreenCapture.ts). -->
          <form @submit.prevent="handleSave()" class="space-y-4" data-screenshot-exclude>
            <StyledInput
              v-model:modelValue="formattedCardNumber"
              label="Número de tarjeta"
              :isRequired="true"
              :maxLength="numberLength + 3"
              placeholder="1234 5678 9012 3456"
            />

            <div class="flex gap-3">
              <div class="flex gap-3 flex-1">
                <StyledInput
                  v-model:modelValue="formModels.expiry.expiration_month"
                  label="Mes"
                  :isRequired="true"
                  :maxLength="2"
                  placeholder="MM"
                />

                <StyledInput
                  v-model:modelValue="formModels.expiry.expiration_year"
                  label="Año"
                  :isRequired="true"
                  :maxLength="2"
                  placeholder="AA"
                />
              </div>

              <div class="flex-1">
                <StyledInput
                  v-model:modelValue="formModels.cvv2"
                  label="CVV"
                  :isRequired="true"
                  :maxLength="cvvLength"
                  placeholder="123"
                  @focus-in="actionBack"
                  @focus-out="actionBack"
                />
              </div>
            </div>

            <div>
              <StyledInput
                v-model:modelValue="formModels.holder_name"
                label="Nombre del titular"
                :isRequired="true"
                placeholder="Como aparece en la tarjeta"
              />
              <StyledCheckbox
                label="Usar el nombre de la cuenta."
                :modelValue="activated"
                @update:modelValue="actionActivated"
                class="mt-2"
              />
            </div>

            <StyledInput
              v-model:modelValue="formModels.alias"
              label="Alias (opcional)"
              placeholder="ej. Mi tarjeta personal"
              maxlength="30"
            />

            <div v-if="canAddCard && canAddCorporateCard">
              <label class="block text-sm font-medium text-gray-700 mb-2">
                Tipo de tarjeta
              </label>
              <div class="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  :disabled="personalCards.length >= 6"
                  :class="[
                    'flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed',
                    addTarget === 'user'
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300',
                  ]"
                  @click="addTarget = 'user'"
                >
                  <span class="material-symbols-outlined text-[18px]">person</span>
                  Personal
                </button>
                <button
                  type="button"
                  :disabled="corporateCards.length >= 6"
                  :class="[
                    'flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border-2 text-sm font-medium transition-all disabled:opacity-40 disabled:cursor-not-allowed',
                    addTarget === 'company'
                      ? 'border-emerald-600 bg-emerald-50 text-emerald-700'
                      : 'border-gray-200 text-gray-600 hover:border-gray-300',
                  ]"
                  @click="addTarget = 'company'"
                >
                  <span class="material-symbols-outlined text-[18px]">business</span>
                  Corporativa
                </button>
              </div>
            </div>

            <p
              v-if="addTarget === 'company' && suggestedAlias"
              class="text-xs text-gray-500"
            >
              Alias sugerido: {{ suggestedAlias }}
            </p>

            <div class="flex justify-end pt-2">
              <StyledButton
                label="Guardar tarjeta"
                class="bg-blue-600 text-white hover:bg-blue-700"
                :loading="createCardLoading"
              />
            </div>
          </form>
        </div>
      </template>
    </ModalComponent>

    <!-- Filter tabs -->
    <div
      v-if="cards.length > 0"
      class="flex flex-wrap gap-2 mb-6"
    >
      <button
        v-for="option in filterOptions"
        :key="option.value"
        type="button"
        :class="[
          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all',
          activeFilter === option.value
            ? option.color === 'blue'
              ? 'bg-blue-50 border-blue-200 text-blue-700'
              : option.color === 'emerald'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                : 'bg-gray-100 border-gray-300 text-gray-900'
            : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300 hover:bg-gray-50',
        ]"
        @click="activeFilter = option.value"
      >
        <span class="material-symbols-outlined text-[16px]">{{ option.icon }}</span>
        {{ option.label }}
        <span
          :class="[
            'ml-0.5 px-1.5 py-0 rounded-full text-[10px]',
            activeFilter === option.value
              ? option.color === 'blue'
                ? 'bg-blue-200 text-blue-800'
                : option.color === 'emerald'
                  ? 'bg-emerald-200 text-emerald-800'
                  : 'bg-gray-300 text-gray-800'
              : 'bg-gray-100 text-gray-600',
          ]"
        >
          {{ option.count() }}
        </span>
      </button>
    </div>

    <!-- Empty state -->
    <div
      v-if="filteredCards.length === 0 && !isLoading"
      class="flex flex-col items-center justify-center py-14 text-center"
    >
      <div class="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center mb-4">
        <span class="material-symbols-outlined text-gray-400 text-3xl">credit_card_off</span>
      </div>
      <h3 class="text-sm font-semibold text-gray-900">
        {{ emptyStateMessages[activeFilter].title }}
      </h3>
      <p class="text-xs text-gray-500 mt-1 max-w-xs">
        {{ emptyStateMessages[activeFilter].body }}
      </p>
      <button
        v-if="activeFilter === 'company' ? canAddCorporateCard : canAddCard"
        type="button"
        class="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
        @click="openAddModal(activeFilter === 'company' ? 'company' : 'user')"
      >
        <span class="material-symbols-outlined text-[16px]">add</span>
        Agregar tarjeta
      </button>
    </div>

    <!-- Cards grid -->
    <div
      v-else
      class="grid grid-cols-1 lg:grid-cols-2 gap-6"
    >
      <div
        v-for="(card, i) in filteredCards"
        :key="card.id || i"
        class="max-w-md"
      >
        <CardComponent
          :cardId="card.id"
          :brand="card.brand"
          :card-number="card.cardNumber"
          :expiration-month="card.expirationMonth"
          :expiration-year="card.expirationYear"
          :holder-name="card.holderName"
          :alias="card.alias"
          :ownership="card.ownership"
          :query-key="card.ownership === 'company' ? ['companyCards', props.user.companyId] : ['userCards', props.user.id]"
        />
      </div>
    </div>
  </ShadowBox>

  <LoadingModal :when="isLoading" />
</template>

<style scoped>
.list-enter-active,
.list-leave-active {
  transition: all 0.5s ease;
}

.list-enter-from,
.list-leave-to {
  opacity: 0;
  transform: translateX(30px);
}
</style>
